import debounce from 'lodash.debounce';
import { ChangeEvent, memo, ReactNode, useCallback, useEffect, useMemo } from 'react';
import { getStoresOptions } from '@/modules/Products/helpers';
import { useStoresSearch } from '@/modules/Products/hooks';
import { store, useAppDispatch, useAppState } from '@/store';
import { Input } from '@/ui';
import { GetStockBalancesListDTO } from '../../dtos/GetStockBalancesListDTO';
import { applyFilterQuery, normalizeFilterValue } from '../../helpers/buildTabQuery';
import { changeQuery, getStockBalances } from '../../feature';
import { selectQuery, stockBalancesSelector } from '../../feature/selectors';
import { StockBalancesFiltersFields, TGetStockBalancesQuery } from '../../helpers/types';
import { StyledFilterRow } from '../../components/styled';
import { StyledInputNumber, StyledSelect } from './StockBalancesFiltersContainerStyles';

// Load the full list of options for the filter dropdowns instead of the default paginated page.
const OPTIONS_FETCH_LIMIT = 1000;

type TProps = {
  children?: ReactNode;
  className?: string;
  regionOptions: { label: string; value: string }[];
  categoryOptions: { label: string; value: string }[];
  filterRevision: number;
};

const StockBalancesFiltersContainer = ({
  children,
  className,
  regionOptions,
  categoryOptions,
  filterRevision,
}: TProps) => {
  const dispatch = useAppDispatch();
  const { query: queryState } = useAppState(stockBalancesSelector);
  const query = queryState ?? {};
  const { storeList, searchStores } = useStoresSearch(OPTIONS_FETCH_LIMIT);

  const fetchList = useCallback(
    (nextQuery: TGetStockBalancesQuery) => {
      const dto = new GetStockBalancesListDTO(nextQuery);
      dispatch(changeQuery(nextQuery));
      dispatch(getStockBalances(dto));
    },
    [dispatch],
  );

  const handleDebounceSearch = useMemo(
    () =>
      debounce((searchParams: TGetStockBalancesQuery) => {
        fetchList(searchParams);
      }, 500),
    [fetchList],
  );

  useEffect(() => {
    handleDebounceSearch.cancel();
  }, [filterRevision, handleDebounceSearch]);

  useEffect(() => {
    return () => {
      handleDebounceSearch.cancel();
    };
  }, [handleDebounceSearch]);

  const getCurrentQuery = () => selectQuery(store.getState()) ?? {};

  const buildNextQuery = (patch: Partial<TGetStockBalancesQuery>) => applyFilterQuery(getCurrentQuery(), patch);

  const applyFiltersImmediately = (patch: Partial<TGetStockBalancesQuery>) => {
    handleDebounceSearch.cancel();
    fetchList(buildNextQuery(patch));
  };

  const applyFiltersDebounced = (patch: Partial<TGetStockBalancesQuery>) => {
    const nextQuery = buildNextQuery(patch);
    dispatch(changeQuery(nextQuery));
    handleDebounceSearch(nextQuery);
  };

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    applyFiltersDebounced({ [name]: value || undefined });
  };

  const onSelectChange = (value: unknown, key: StockBalancesFiltersFields) => {
    applyFiltersImmediately({ [key]: normalizeFilterValue(value) });
  };

  const handleNumberChange = (name: StockBalancesFiltersFields, value: number | string | null) => {
    applyFiltersImmediately({ [name]: typeof value === 'number' ? value : undefined });
  };

  return (
    <thead className={className}>
      {children}
      <StyledFilterRow>
        <th>
          <Input
            name={StockBalancesFiltersFields.SEARCH}
            value={query.search ?? ''}
            onChange={handleSearch}
            size="large"
            allowClear
          />
        </th>
        <th>
          <Input
            name={StockBalancesFiltersFields.SKU}
            value={query.sku ?? ''}
            onChange={handleSearch}
            size="large"
            allowClear
          />
        </th>
        <th>
          <Input
            name={StockBalancesFiltersFields.EXTERNAL_ID}
            value={query.external_id ?? ''}
            onChange={handleSearch}
            size="large"
            allowClear
          />
        </th>
        <th>
          <StyledSelect
            allowClear
            showSearch
            options={regionOptions}
            value={query.region_id}
            onChange={(value) => onSelectChange(value, StockBalancesFiltersFields.REGION_ID)}
            size="large"
          />
        </th>
        <th>
          <StyledSelect
            allowClear
            showSearch
            isMultiselect
            maxTagCount={1}
            options={categoryOptions}
            value={query.category_ids}
            onChange={(value) => onSelectChange(value, StockBalancesFiltersFields.CATEGORY_IDS)}
            size="large"
          />
        </th>
        <th>
          <StyledSelect
            allowClear
            showSearch
            isMultiselect
            maxTagCount={1}
            options={getStoresOptions(storeList ?? [])}
            value={query.store_ids}
            onSearch={searchStores}
            onChange={(value) => onSelectChange(value, StockBalancesFiltersFields.STORE_IDS)}
            size="large"
          />
        </th>
        <th />
        <th />
        <th>
          <StyledInputNumber
            min={0}
            step={0.01}
            precision={2}
            value={query.price_min}
            onChange={(value) => handleNumberChange(StockBalancesFiltersFields.PRICE_MIN, value)}
            style={{ width: '100%' }}
          />
        </th>
        <th>
          <StyledInputNumber
            min={0}
            step={0.01}
            precision={2}
            value={query.discount_price_min}
            onChange={(value) => handleNumberChange(StockBalancesFiltersFields.DISCOUNT_PRICE_MIN, value)}
            style={{ width: '100%' }}
          />
        </th>
        <th />
      </StyledFilterRow>
    </thead>
  );
};

export default memo(StockBalancesFiltersContainer);
